import { useState } from 'react';
import {
  Search,
  Toggle,
  TextInput,
  Tag,
  InlineLoading,
  InlineNotification,
  Button,
} from '@carbon/react';
import { Filter, Reset } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { knowledgeSearchApi } from '@/lib/api/knowledge-search.api';
import type { ClaimHit } from '@snxt/snxt-onto-share';
import './FacetSearch.scss';

interface ClaimsFacetProps {
  kbId: string;
}

export function ClaimsFacet({ kbId }: ClaimsFacetProps) {
  const [q, setQ] = useState('');
  const [numericOnly, setNumericOnly] = useState(false);
  const [unit, setUnit] = useState('');
  // "submitted" params — only update when user hits Search
  const [params, setParams] = useState<{ q: string; numericOnly: boolean; unit: string } | null>(null);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['knowledge-search-claims', kbId, params],
    queryFn: () =>
      knowledgeSearchApi.searchClaims({
        kbId,
        q: params?.q || undefined,
        numericOnly: params?.numericOnly,
        unit: params?.unit || undefined,
        limit: 200,
      }),
    enabled: params !== null,
  });

  const handleSearch = () => {
    setParams({ q: q.trim(), numericOnly, unit: unit.trim() });
  };

  const handleReset = () => {
    setQ('');
    setNumericOnly(false);
    setUnit('');
    setParams(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const hits: ClaimHit[] = data?.hits ?? [];

  return (
    <div className="facet-search__panel">
      {/* Filters bar */}
      <div className="facet-search__filters">
        <Search
          id="claims-search-q"
          labelText="Search claims"
          placeholder="Filter subject / predicate / object…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={handleKeyDown}
          size="md"
          className="facet-search__text-filter"
        />

        <TextInput
          id="claims-unit-filter"
          labelText="Unit filter"
          placeholder="e.g. K, mol, eV, %"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          onKeyDown={handleKeyDown}
          size="md"
          className="facet-search__unit-input"
        />

        <div className="facet-search__toggle-row">
          <Toggle
            id="claims-numeric-toggle"
            labelText="Numeric claims only"
            toggled={numericOnly}
            onToggle={(checked) => setNumericOnly(checked)}
            size="sm"
          />
        </div>

        <Button
          kind="primary"
          size="md"
          renderIcon={Filter}
          onClick={handleSearch}
          disabled={isLoading || isFetching}
        >
          Search
        </Button>

        <Button
          kind="ghost"
          size="md"
          renderIcon={Reset}
          onClick={handleReset}
          hasIconOnly
          iconDescription="Reset filters"
        />
      </div>

      {/* Status / results */}
      {(isLoading || isFetching) && (
        <InlineLoading description="Searching claims…" />
      )}

      {isError && (
        <InlineNotification
          kind="error"
          title="Search failed"
          hideCloseButton
        />
      )}

      {params && !isLoading && !isFetching && !isError && hits.length === 0 && (
        <div className="facet-search__empty">No claims found for these filters.</div>
      )}

      {hits.length > 0 && (
        <>
          <div className="facet-search__count">
            {hits.length} claim{hits.length !== 1 ? 's' : ''}
            {params?.numericOnly && (
              <Tag size="sm" type="cyan" className="facet-search__active-filter">
                numeric only
              </Tag>
            )}
            {params?.unit && (
              <Tag size="sm" type="cyan" className="facet-search__active-filter">
                unit: {params.unit}
              </Tag>
            )}
          </div>

          <div className="facet-search__table-wrapper">
            <table className="facet-search__table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Predicate</th>
                  <th>Object</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {hits.map((h, i) => (
                  <tr key={`${h.extraction_id}-${h.claim_index}-${i}`}
                      className={h.is_numeric ? 'facet-search__row--numeric' : ''}>
                    <td className="facet-search__cell">{h.subject}</td>
                    <td className="facet-search__cell facet-search__cell--predicate">{h.predicate}</td>
                    <td className="facet-search__cell">
                      {h.object}
                      {h.is_numeric && (
                        <Tag size="sm" type="magenta" className="facet-search__numeric-tag">
                          numeric
                        </Tag>
                      )}
                    </td>
                    <td className="facet-search__cell facet-search__cell--source">
                      <span title={h.source_snippet ?? ''}>
                        {h.knowledge_object_name ?? h.knowledge_object_id}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// Made with Bob
